import { useEffect, useMemo, useRef, useState } from 'react';
import { Stack, Text, Group, Button, Title, Anchor, Skeleton } from '@repletoryreach/sdk-ui';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { TextInput, PasswordInput, Textarea, Switch, Select } from '@repletoryreach/sdk-ui/inputs';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import type { admin, settings } from '@repletoryreach/sdk-api';
import { useIntegration, useUpsertIntegration } from '../../../../data/useIntegrations';
import { useT } from '../../../../i18n/I18nProvider';
import { RemoteSelect } from './RemoteSelect';

interface Props {
  provider: admin.IntegrationProvider | null;
  onClose: () => void;
}

export function EditIntegrationDrawer({ provider, onClose }: Props) {
  const t = useT();
  const open = !!provider;
  const code = provider?.code ?? null;
  const { data, isLoading, error } = useIntegration(code, /* reveal */ true);
  const upsert = useUpsertIntegration();

  const [config, setConfig] = useState<Record<string, string>>({});
  const [enabled, setEnabled] = useState(false);
  const [notes, setNotes] = useState('');
  const [dirty, setDirty] = useState(false);

  // The reset effect used to depend on the query object, so any background
  // invalidation replaced hand-typed credentials with the server's values.
  // Once the operator has typed something, the server no longer wins.
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;

  const row = data?.data;
  useEffect(() => {
    if (!open) return;
    if (dirtyRef.current) return;
    const initial: Record<string, string> = {};
    for (const [k, v] of Object.entries(row?.config ?? {})) {
      initial[k] = v == null ? '' : String(v);
    }
    setConfig(initial);
    setEnabled(row?.enabled ?? false);
    setNotes(row?.notes ?? '');
  }, [open, code, row]);

  useEffect(() => { if (!open) setDirty(false); }, [open]);

  // A malformed seed row would otherwise throw inside the render and blank the
  // drawer with no explanation. Read through the settings module's type,
  // which knows the `select_remote` kind and its `source` / `depends_on`.
  const fields = useMemo<settings.IntegrationFieldSchema[]>(
    () => (Array.isArray(provider?.field_schema) ? provider.field_schema : []),
    [provider]
  );
  const labels = useMemo(() => Object.fromEntries(fields.map((f) => [f.key, f.label])), [fields]);

  const missing = useMemo(
    () => fields.filter((f) => f.required && !(config[f.key] ?? '').trim()).map((f) => f.label),
    [fields, config]
  );

  function setField(key: string, value: string) {
    setDirty(true);
    setConfig((cur) => ({ ...cur, [key]: value }));
  }

  /** Credentials are typed by hand from another vendor's console; losing them
      to a stray Escape means fetching them again from there. */
  function requestClose() {
    if (!dirty) { onClose(); return; }
    openConfirmModal({
      title: t('settings.integrations.form.discard.title'),
      children: <p style={{ margin: 0 }}>{t('settings.integrations.form.discard.body')}</p>,
      labels: { confirm: t('settings.integrations.form.discard.confirm'), cancel: t('settings.integrations.form.discard.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => { setDirty(false); onClose(); }
    });
  }

  async function save() {
    if (!provider) return;
    try {
      await upsert.mutateAsync({
        providerCode: provider.code,
        config,
        enabled,
        notes: notes || null
      });
      setDirty(false);
      notifySuccess(t('settings.integrations.toast.saved', { name: provider.display_name }), t('settings.integrations.toast.savedTitle'));
      onClose();
    } catch (e) {
      notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.integrations.toast.saveFailed'));
    }
  }

  return (
    <Drawer
      opened={open}
      onClose={requestClose}
      title={provider ? t('settings.integrations.form.title', { name: provider.display_name }) : ''}
      position="right"
      size="md"
    >
      {provider && (
        <Stack gap="md">
          {provider.description && (
            <Text role="bodySmall" c="var(--rr-color-text-muted)">{provider.description}</Text>
          )}
          {provider.docs_url && (
            <Anchor href={provider.docs_url} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>
              {t('settings.integrations.providerDocs')}
            </Anchor>
          )}

          {/* Opening this drawer asks the API for the stored secrets in the
              clear, so the operator should know the screen now holds them. */}
          <Alert color="warning">{t('settings.integrations.form.revealNotice')}</Alert>

          {error && <Alert color="error">{t('settings.integrations.form.loadError')}</Alert>}
          {isLoading && <Skeleton height={140} />}

          {!isLoading && (
            <Stack gap="md">
              {fields.map((f) => f.kind === 'select_remote' ? (
                <RemoteSelect
                  key={f.key}
                  providerCode={provider.code}
                  field={f}
                  labels={labels}
                  config={config}
                  value={config[f.key] ?? ''}
                  onChange={(v) => setField(f.key, v)}
                />
              ) : (
                <FieldInput
                  key={f.key}
                  field={f}
                  value={config[f.key] ?? ''}
                  onChange={(v) => setField(f.key, v)}
                />
              ))}
            </Stack>
          )}

          <Stack gap="md" style={{ paddingTop: 8, borderTop: '1px solid var(--rr-color-border)' }}>
            <Title order={6}>{t('settings.integrations.form.operational')}</Title>
            <Switch
              label={t('common.enabled')}
              description={t('settings.integrations.form.enabledHint')}
              checked={enabled}
              onChange={(next) => { setDirty(true); setEnabled(next); }}
            />
            <Textarea
              label={t('settings.integrations.form.notes')}
              placeholder={t('settings.integrations.form.notesPlaceholder')}
              value={notes}
              onChange={(e) => { setDirty(true); setNotes(e.currentTarget.value); }}
              minRows={2}
            />
          </Stack>

          {missing.length > 0 && (
            <Alert color="warning">
              {t('settings.integrations.form.missingRequired', { fields: missing.join(', ') })}
            </Alert>
          )}

          <Group justify="flex-end" style={{ paddingTop: 12, borderTop: '1px solid var(--rr-color-border)' }}>
            <Button variant="default" onClick={requestClose}>{t('action.cancel')}</Button>
            <Button onClick={save} loading={upsert.isPending}>{t('action.save')}</Button>
          </Group>
        </Stack>
      )}
    </Drawer>
  );
}

interface FieldProps {
  field: settings.IntegrationFieldSchema;
  value: string;
  onChange: (v: string) => void;
}

function FieldInput({ field, value, onChange }: FieldProps) {
  /*
   * The hint stays under the control. It used to be rendered twice, once as a
   * description and once as a placeholder that vanished the moment the field
   * was typed into, through a heuristic on the words "e.g.".
   */
  const common = {
    label: field.label,
    description: field.description,
    required: !!field.required,
    value,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      onChange(e.currentTarget.value)
  };

  if (field.kind === 'password') return <PasswordInput {...common} />;
  if (field.kind === 'enum') {
    const opts = (field.options ?? []).map((o) => ({ value: o, label: o }));
    return <Select {...common} data={[{ value: '', label: '–' }, ...opts]} />;
  }
  return <TextInput {...common} type={field.kind === 'url' ? 'url' : 'text'} />;
}
